enum PersistentStorageEnums {
  MRU_FILES = 'MRUFiles',
}

// PersistentStorage provides the session and local storage capabilities of Notebook
export default class PersistentStorage<T> {
  // The maximum number of tracked items to keep in session and local storage.
  // Each browser storage at the time of writing has a space limit of 5MB.
  MAX_TRACKED_FILES_COUNT = 500;

  getFileStorage(fileId: string): T | undefined {
    const sessionFileStorageString = sessionStorage.getItem(fileId);
    const localFileStorageString = localStorage.getItem(fileId);

    // Returns the persisted state of the current file for use in the respective feature
    // ex. worksheet step state with persisted step id
    const fileStorageString = sessionFileStorageString || localFileStorageString;
    if (fileStorageString === 'undefined') {
      return undefined;
    }
    return fileStorageString ? JSON.parse(fileStorageString) : undefined;
  }

  // Some persistent storage types could pass in undefined to indicate a cleared state, which we facilitate by
  // removing the respective item's storage state in the else{} below (resulting in null for state value).
  // ex. see WorksheetPersistentStorage.setSelectedStepId()
  setFileStorage(fileId: string, storageState: T | undefined): void {
    if (storageState) {
      sessionStorage.setItem(fileId, JSON.stringify(storageState));
      localStorage.setItem(fileId, JSON.stringify(storageState));
    } else {
      this._removeFileFromStorage(fileId);
    }
    this._updateMRUFiles(fileId);
  }

  // updates a browser-based MRU cache for the most recently used items, removing the least recently used
  // items from session and local storage if the cache exceeds the length of MAX_TRACKED_FILES_COUNT
  _updateMRUFiles(fileId: string): void {
    let MRUFiles = this._getMRUFiles();

    // remove fileId from the list if it exists, then add it to the front of the MRU cache
    MRUFiles = MRUFiles.filter((itemId: string) => itemId !== fileId);
    MRUFiles.unshift(fileId);

    // remove the least recently used files that exceed the maximum tracked files count
    const toDeleteFileIds = MRUFiles.slice(this.MAX_TRACKED_FILES_COUNT);

    toDeleteFileIds.forEach((fileId: string) => {
      this._removeFileFromStorage(fileId);
    });

    this._setMRUFiles(MRUFiles.slice(0, this.MAX_TRACKED_FILES_COUNT));
  }

  _getMRUFiles(): Array<string> {
    const MRUFilesString = localStorage.getItem(PersistentStorageEnums.MRU_FILES);
    return MRUFilesString ? JSON.parse(MRUFilesString) : [];
  }

  _setMRUFiles(MRUFiles: Array<string>): void {
    localStorage.setItem(PersistentStorageEnums.MRU_FILES, JSON.stringify(MRUFiles));
    sessionStorage.setItem(PersistentStorageEnums.MRU_FILES, JSON.stringify(MRUFiles));
  }

  _removeFileFromStorage(fileId: string): void {
    sessionStorage.removeItem(fileId);
    localStorage.removeItem(fileId);
  }
}
